import { useCallback, useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'

import { CatchError } from 'Exceptions/exceptions'
import { DEFAULT_INQUIRY_CALLBACK_FILTERS } from 'Main/Constant'
import InquiryCallbackService from 'Main/service/InquiryCallbackService'
import { displayDate, statusClass } from 'Main/Utils'
import CommonListApiPaginated from 'views/components/Table/CommonListApiPaginated'
import LogUserFilter from 'views/components/Input/LogUserFilter'
import useLogUserFilter from 'views/dashboard/logs/useLogUserFilter'
import { getRefreshToken } from 'variables/common'

// NOTE: CommonListApiPaginated is assumed to call `getData(page, size)` and
// to manage its own loading / empty / pagination UI internally (mirrors
// InquiryConfigList's usage). If its actual signature differs, adjust
// `getCallbacks` below accordingly.

function InquiryCallbackManager () {
  const navigate = useNavigate()
  const refreshToken = getRefreshToken()
  const inquiryCallbackService = useMemo(() => new InquiryCallbackService(), [])
  const { isSuperAdmin, users, loadingUsers } = useLogUserFilter()

  // `formFilters` is the live draft bound to the inputs; `appliedFilters` is
  // what's actually sent to the API and only changes on Apply/Clear/user
  // switch, so typing in a field doesn't trigger a refetch on every keystroke.
  const [formFilters, setFormFilters] = useState(DEFAULT_INQUIRY_CALLBACK_FILTERS)
  const [appliedFilters, setAppliedFilters] = useState(DEFAULT_INQUIRY_CALLBACK_FILTERS)
  const [reloadKey, setReloadKey] = useState(0)
  const [filtersOpen, setFiltersOpen] = useState(false)
  const [options, setOptions] = useState({ deviceNames: [], inquiryTypes: [], statuses: [] })

  const loadFilterOptions = useCallback(async (userId = 'ALL') => {
    try {
      const response = await inquiryCallbackService.getData({ page: 1, size: 200, userId })
      const rows = response?.data?.result || []
      setOptions({
        deviceNames: [...new Set(rows.map((row) => row.device_name).filter(Boolean))].sort(),
        inquiryTypes: [...new Set(rows.map((row) => row.inquiryType).filter(Boolean))].sort(),
        statuses: [...new Set(rows.map((row) => row.status_code).filter(Boolean))].sort()
      })
    } catch {
      setOptions({ deviceNames: [], inquiryTypes: [], statuses: [] })
    }
  }, [inquiryCallbackService])

  useEffect(() => {
    loadFilterOptions('ALL')
  }, [loadFilterOptions])

  const getCallbacks = useCallback((page, size) => inquiryCallbackService.getData({
    page,
    size,
    trxId: appliedFilters.trxId,
    deviceName: appliedFilters.deviceName,
    inquiryType: appliedFilters.inquiryType,
    status: appliedFilters.status,
    from: appliedFilters.from,
    to: appliedFilters.to,
    userId: appliedFilters.userId
  }), [inquiryCallbackService, appliedFilters])

  const handleListError = useCallback((error) => {
    CatchError(error, navigate, refreshToken)
  }, [navigate, refreshToken])

  const setField = (field) => (event) => {
    setFormFilters((current) => ({ ...current, [field]: event.target.value }))
  }

  const changeUser = (event) => {
    const nextFilters = { ...formFilters, userId: event.target.value }
    setFormFilters(nextFilters)
    setAppliedFilters(nextFilters)
    setReloadKey((current) => current + 1)
    loadFilterOptions(nextFilters.userId)
  }

  const applyFilters = (event) => {
    event.preventDefault()
    setAppliedFilters(formFilters)
    setReloadKey((current) => current + 1)
  }

  const clearFilters = () => {
    setFormFilters(DEFAULT_INQUIRY_CALLBACK_FILTERS)
    setAppliedFilters(DEFAULT_INQUIRY_CALLBACK_FILTERS)
    setReloadKey((current) => current + 1)
    loadFilterOptions('ALL')
  }

  const columns = useMemo(() => [
    {
      header: 'Device',
      accessorKey: 'device_name',
      maxWidth: 150,
      cell: ({ getValue }) => getValue() || '—'
    },
    {
      header: 'Transaction ID',
      accessorKey: 'trxId',
      maxWidth: 150,
      cell: ({ getValue }) => <code>{getValue() || '—'}</code>
    },
    {
      header: 'Type',
      accessorKey: 'inquiryType',
      maxWidth: 90,
      cell: ({ getValue }) => getValue() || '—'
    },
    {
      header: 'Endpoint',
      accessorKey: 'endpoint',
      cell: ({ getValue }) => <code title={getValue()}>{getValue() || '—'}</code>
    },
    {
      header: 'Status',
      accessorKey: 'status_code',
      maxWidth: 90,
      cell: ({ getValue }) => <span className={`status-${statusClass(getValue())}`}>{getValue() || 'Unknown'}</span>
    },
    {
      header: 'Received',
      accessorKey: 'creation',
      cell: ({ row }) => displayDate(row.original.creation || row.original.date)
    },
    {
      header: 'Inqury State',
      accessorKey: 'status_inqury',
      maxWidth: 125,
      cell: ({ getValue }) => <span className={`status-${statusClass(getValue())}`}>{getValue() || 'Unknown'}</span>
    },
    {
      header: 'Details',
      id: 'actions',
      enableSorting: false,
      filterable: false,
      exportable: false,
      maxWidth: 80,
      cell: ({ row }) => (
        <button
          type="button"
          className="btn btn-sm btn-primary"
          onClick={() => navigate(`/dashboard/inquiry-callback/${row.original.id}`)}
        >
          <i className="fa-solid fa-arrow-up-right-from-square" />
        </button>
      )
    }
  ], [navigate])

  const filterToggle = (
    <button
      type="button"
      className="btn btn-outline-primary"
      onClick={() => setFiltersOpen((current) => !current)}
      aria-expanded={filtersOpen}
      aria-controls="inquiry-callback-filters"
    >
      <i className={`fa-solid ${filtersOpen ? 'fa-angle-up' : 'fa-filter'} me-1`} />
      {filtersOpen ? 'Hide filters' : 'Show filters'}
    </button>
  )

  return (
    <div className="page-content">
      <section>
        <div className="card mb-3">
          <div className="card-header d-flex justify-content-between align-items-center flex-wrap gap-2">
            <div>
              <h5 className="card-title mb-1"><i className="fa-solid fa-magnifying-glass-dollar me-2" />Inquiry activity</h5>
            </div>
            {filterToggle}
          </div>

          <div className={`collapse ${filtersOpen ? 'show' : ''}`} id="inquiry-callback-filters">
            <div className="card-body">
              <form className="row g-2" onSubmit={applyFilters}>
                {isSuperAdmin && (
                  <LogUserFilter
                    value={formFilters.userId}
                    users={users}
                    loading={loadingUsers}
                    onChange={changeUser}
                    className="col-12 col-lg-3"
                  />
                )}
                <div className="col-12 col-lg-3">
                  <label htmlFor="inquiry-trx" className="form-label">Transaction ID</label>
                  <input id="inquiry-trx" className="form-control" value={formFilters.trxId} onChange={setField('trxId')} placeholder="Search transaction ID" />
                </div>
                <div className="col-6 col-lg-2">
                  <label htmlFor="inquiry-device" className="form-label">Device</label>
                  <select id="inquiry-device" className="form-control" value={formFilters.deviceName} onChange={setField('deviceName')}>
                    <option value="%">All devices</option>
                    {options.deviceNames.map((value) => <option key={value} value={value}>{value}</option>)}
                  </select>
                </div>
                <div className="col-6 col-lg-2">
                  <label htmlFor="inquiry-type" className="form-label">Type</label>
                  <select id="inquiry-type" className="form-control" value={formFilters.inquiryType} onChange={setField('inquiryType')}>
                    <option value="%">All types</option>
                    {options.inquiryTypes.map((value) => <option key={value} value={value}>{value}</option>)}
                  </select>
                </div>
                <div className="col-6 col-lg-2">
                  <label htmlFor="inquiry-status" className="form-label">Status</label>
                  <select id="inquiry-status" className="form-control" value={formFilters.status} onChange={setField('status')}>
                    <option value="%">All statuses</option>
                    {options.statuses.map((value) => <option key={value} value={value}>{value}</option>)}
                  </select>
                </div>
                <div className="col-6 col-lg-2">
                  <label htmlFor="inquiry-from" className="form-label">From</label>
                  <input id="inquiry-from" type="date" className="form-control" value={formFilters.from} max={formFilters.to} onChange={setField('from')} />
                </div>
                <div className="col-6 col-lg-2">
                  <label htmlFor="inquiry-to" className="form-label">To</label>
                  <input id="inquiry-to" type="date" className="form-control" value={formFilters.to} min={formFilters.from} onChange={setField('to')} />
                </div>
                <div className="col-12 col-lg-2 d-flex align-items-end gap-2">
                  <button type="submit" className="btn btn-primary flex-fill"><i className="fa-solid fa-filter me-1" />Apply</button>
                  <button type="button" className="btn btn-secondary" onClick={clearFilters} title="Clear filters"><i className="fa-solid fa-rotate-left" /></button>
                </div>
              </form>
            </div>
          </div>
          <CommonListApiPaginated
            header=" "
            subtitle=""
            columns={columns}
            getData={getCallbacks}
            reloadKey={reloadKey}
            loadingLabel="Loading inquiry activity..."
            emptyLabel="No inquiry callbacks match these filters."
            onError={handleListError}
          />

        </div>
      </section>
    </div>
  )
}

export default InquiryCallbackManager